import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, X, ChevronRight } from 'lucide-react';
import { colorForKind, SpanKindIcon } from 'shared/components/spans/KindTag';
import { spanMatches, filterTree } from 'features/agent-analytics/components/traceFilter';

function ms(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (v < 1000) return `${Math.round(v)} ms`;
  return `${(v / 1000).toFixed(2)} s`;
}

function fmtInt(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString() : '0';
}

// Token counts for a crowded row: exact below 10k, then 39.8k / 1.2M. The exact
// figure is always one hover away.
function tok(n) {
  const v = Number(n) || 0;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (v >= 1e4) return `${(v / 1e3).toFixed(1).replace(/\.0$/, '')}k`;
  return v.toLocaleString();
}

// Tighter still for the timeline's one line, which shares space with the name:
// 8,338 → 8.3k.
function tokShort(n) {
  const v = Number(n) || 0;
  return v >= 1000 && v < 1e4 ? `${(v / 1e3).toFixed(1).replace(/\.0$/, '')}k` : tok(v);
}

function tokensOf(node) {
  const input = Number(node.input_tokens) || 0;
  const output = Number(node.output_tokens) || 0;
  return { input, output, total: input + output };
}

/** Total tokens as one figure, `Σ 8,338 tok`; the split is in the hover card. */
function TokenSum({ total, short = false }) {
  return (
    <span className="whitespace-nowrap text-[color:var(--text-muted)]">
      <span className="text-[color:var(--text-disabled)]">Σ</span> {short ? tokShort(total) : tok(total)} tok
    </span>
  );
}

// Row cost at 3 significant figures ($0.098445 → $0.0984, $1.2345 → $1.23):
// enough to compare rows at a glance. The exact figure is in the hover. Null for 0.
function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return null;
  return `$${Number(v.toPrecision(3)).toLocaleString('en-US', { maximumFractionDigits: 20 })}`;
}

// Exact USD for hovers: up to 8 decimals, trailing zeros trimmed.
function moneyExact(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '$0';
  return `$${v.toFixed(8).replace(/0+$/, '').replace(/\.$/, '')}`;
}

// Subtree cost = this span's own cost + every descendant's cost.
function subtreeCost(node) {
  let c = Number(node.cost) || 0;
  (node.children || []).forEach((ch) => { c += subtreeCost(ch); });
  return c;
}

// Latency heat colour relative to the slowest span in the trace.
function durationColor(durationMs, maxMs) {
  const r = maxMs > 0 ? (Number(durationMs) || 0) / maxMs : 0;
  if (r >= 0.6) return 'var(--feedback-error)';
  if (r >= 0.4) return 'var(--feedback-warning)';
  return 'var(--text-muted)';
}

function buildTree(spans) {
  const byId = new Map();
  spans.forEach((s) => byId.set(s.span_id, { ...s, children: [] }));
  const roots = [];
  byId.forEach((node) => {
    if (node.parent_span_id && byId.has(node.parent_span_id)) {
      byId.get(node.parent_span_id).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const byStart = (a, b) => new Date(a.started_at) - new Date(b.started_at);
  const sort = (nodes) => { nodes.sort(byStart); nodes.forEach((n) => sort(n.children)); };
  sort(roots);
  return roots;
}

function computeWindow(spans) {
  let min = Infinity, max = -Infinity;
  spans.forEach((s) => {
    const start = new Date(s.started_at).getTime();
    const end = s.ended_at ? new Date(s.ended_at).getTime() : start + (s.duration_ms || 0);
    if (Number.isFinite(start) && start < min) min = start;
    if (Number.isFinite(end) && end > max) max = end;
  });
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return { min: 0, max: 1 };
  return { min, max };
}

const ROW_HEIGHT = 30;
const DEFAULT_PX_PER_MS = 0.04;
const MAX_PX_PER_MS = 2.0;
const TARGET_MIN_PX = 720;

function computePxPerMs(totalMs) {
  if (!Number.isFinite(totalMs) || totalMs <= 0) return DEFAULT_PX_PER_MS;
  if (totalMs * DEFAULT_PX_PER_MS >= TARGET_MIN_PX) return DEFAULT_PX_PER_MS;
  return Math.min(MAX_PX_PER_MS, TARGET_MIN_PX / totalMs);
}

function pickTickInterval(totalSeconds) {
  if (totalSeconds <= 3)   return 0.5;
  if (totalSeconds <= 8)   return 1;
  if (totalSeconds <= 20)  return 2;
  if (totalSeconds <= 60)  return 5;
  if (totalSeconds <= 180) return 15;
  if (totalSeconds <= 600) return 30;
  return 60;
}

function TimeAxis({ totalMs, pxPerMs }) {
  const totalSec = totalMs / 1000;
  const step = pickTickInterval(totalSec);
  const ticks = [];
  for (let s = 0; s <= totalSec + 0.001; s += step) ticks.push(s);
  return (
    <div className="relative h-6 border-b border-[color:var(--border-default)] bg-[color:var(--surface-sunken)]" style={{ width: totalMs * pxPerMs }}>
      {ticks.map((sec, i) => (
        <div key={i} className="absolute top-0 h-full border-l border-[color:var(--border-default)]" style={{ left: sec * 1000 * pxPerMs }}>
          <span className="absolute left-1.5 top-1 text-[10px] tabular-nums text-[color:var(--text-muted)]">{sec.toFixed(2)}s</span>
        </div>
      ))}
    </div>
  );
}

const GUIDE_INDENT = 16;
// Token, not a white alpha. The old `rgba(255,255,255,0.25)` was white-on-white
// in light mode — the guides simply weren't there, which is a large part of why
// the tree read as ragged rather than nested. `strong`, because `default` all
// but vanished on the dark surface.
const GUIDE_LINE = '1px solid var(--border-strong)';
// The elbow crosses the row's `gap-1.5` (6px) and the 3px of padding around the
// 14px glyph in its 20px slot, stopping 2px short of the glyph itself.
const GUIDE_REACH = -7;
// The stem leaves 2px under the glyph too: half the 14px glyph plus 2.
const STEM_TOP = 'calc(50% + 9px)';

/**
 * The line from under an open parent's glyph to the bottom of its row.
 *
 * The children's connectors start at the top of their own rows, so without it
 * every branch began ~10px below the parent's icon — the gap between the glyph
 * and its row's bottom edge — and the tree looked broken at each level.
 */
function Stem() {
  return <span aria-hidden className="pointer-events-none absolute bottom-0 left-1/2" style={{ top: STEM_TOP, borderLeft: GUIDE_LINE }} />;
}

/**
 * Where a guide column's vertical line sits.
 *
 * At the column's RIGHT edge, not its centre. The row lays out as
 * `[guides: depth × 16px] gap-6 [icon 20px] gap-6 [name]`, so the icon of a node
 * at depth d is centred at `d × 16 + 6 + 10` = `(d + 1) × 16` — exactly the
 * right edge of guide column d. Drawing at `left: 50%` put every vertical 8px
 * to the left of the icon it was supposed to descend from, so branches appeared
 * to hang off nothing. Anchoring to the right edge makes each line drop out of
 * its parent's icon, which is what a trace tree is supposed to show.
 */
const GUIDE_X = '100%';

// Flatten the tree to the currently-visible rows (respecting collapse), with the
// guide-line metadata needed to draw tree connectors next to each row.
function flattenVisible(roots, collapsed) {
  const rows = [];
  const walk = (nodes, depth, ancestorLines) => {
    nodes.forEach((node, i) => {
      const isLast = i === nodes.length - 1;
      const kids = node.children || [];
      const open = !collapsed.has(node.span_id) && kids.length > 0;
      // The synthetic run row is a wrapper, not a step: when it holds a single
      // root span, that span IS the run and the two belong on the same line.
      // Spending an indent level on the wrapper pushed the whole tree one
      // notch right and made the real root look like a child of itself.
      //
      // With several root spans the wrapper earns its level — that is what
      // gives them a shared trunk to hang off instead of standing as
      // unconnected stumps.
      const transparent = node._wrapper && kids.length === 1;
      // `stem`: children hang one level in, so a line drops from this glyph.
      rows.push({ node, depth, ancestorLines, isLast, stem: open && !transparent });
      if (open) {
        if (transparent) walk(kids, depth, ancestorLines);
        else walk(kids, depth + 1, [...ancestorLines, !isLast]);
      }
    });
  };
  walk(roots, 0, []);
  return rows;
}

/**
 * Tree connector lines for one row.
 *
 * Two different things live in a guide column, and confusing them is what makes
 * a tree look shattered:
 *
 *   - Column `c` sits at the icon centre of the ancestor at depth `c`, and the
 *     vertical it draws is *that ancestor's spine* — the line joining its
 *     children.
 *   - That spine must keep going past this row exactly when there are more
 *     children still to come, i.e. when OUR ancestor at depth `c + 1` is not
 *     its parent's last child.
 *
 * `flattenVisible` appends `!isLast` on the way down, so `ancestorLines[i]` is
 * "the depth-`i` ancestor has siblings after it" — and the spine test is
 * therefore `ancestorLines[c + 1]`, not `ancestorLines[c]`.
 *
 * Getting that wrong drops the spine for every row inside a non-final subtree:
 * descend into a middle child and the parent's line vanishes for the length of
 * that subtree, so the siblings below it appear to float unattached.
 *
 * The rightmost column draws this node's own connector: a full-height │ with a
 * tick for ├─, or a rounded elbow for └─.
 */
function Guides({ depth, ancestorLines, isLast }) {
  if (!depth) return null;
  const cols = [];
  for (let c = 0; c < depth; c += 1) {
    const isConnector = c === depth - 1;
    cols.push(
      <span key={c} className="relative flex-none self-stretch" style={{ width: GUIDE_INDENT }}>
        {/* Pass-through │: the depth-c ancestor's spine, still running because
            our depth-(c+1) ancestor has siblings after it. */}
        {!isConnector && ancestorLines[c + 1] ? (
          <span className="absolute inset-y-0" style={{ left: GUIDE_X, borderLeft: GUIDE_LINE }} />
        ) : null}
        {isConnector ? (
          isLast ? (
            // └ as one box with two borders and a rounded corner, rather than a
            // vertical and a horizontal meeting at a hard right angle.
            <span
              className="absolute top-0"
              style={{
                left: GUIDE_X,
                right: GUIDE_REACH,
                height: '50%',
                borderLeft: GUIDE_LINE,
                borderBottom: GUIDE_LINE,
                borderBottomLeftRadius: 6,
              }}
            />
          ) : (
            <>
              <span className="absolute inset-y-0" style={{ left: GUIDE_X, borderLeft: GUIDE_LINE }} />
              <span
                className="absolute"
                style={{ left: GUIDE_X, right: GUIDE_REACH, top: '50%', borderTop: GUIDE_LINE }}
              />
            </>
          )
        ) : null}
      </span>,
    );
  }
  return <>{cols}</>;
}


// Count every node in a tree (post-filter span count for the header).
function countNodes(nodes) {
  return (nodes || []).reduce((acc, n) => acc + 1 + countNodes(n.children), 0);
}

const NO_COLLAPSE = new Set();
// Sentinel span_id for the synthetic run-level node used while searching.
const RUN_NODE_ID = '__run__';

function hasFailedDescendant(node) {
  return (node.children || []).some((c) => c.status === 'error' || hasFailedDescendant(c));
}

/**
 * Failed steps are tinted red down the tree and timeline, with a red edge that
 * survives selection, so the step that broke is found by eye — the small
 * "error" tag beside the name was easy to scroll past.
 */
function rowTone(isSelected, isError) {
  if (isSelected) return 'bg-[color:var(--surface-hover)]';
  if (isError) return 'bg-[color:var(--feedback-error-bg)]';
  return 'hover:bg-[color:var(--surface-sunken)]';
}

function errorEdge(isError) {
  return isError ? { boxShadow: 'inset 2px 0 0 var(--feedback-error)' } : null;
}

function FailureDot() {
  return (
    <span
      className="ml-auto h-1.5 w-1.5 flex-none rounded-full bg-[color:var(--feedback-error)]"
      title="A step inside failed"
      aria-label="A step inside failed"
      role="img"
    />
  );
}

// Flat tree row — uses the same flattenVisible output as Timeline so connector
// lines (Guides) appear on the left of the icon and the chevron sits on the right.
function TreeRow({ row, onSelect, selectedId, maxMs, collapsed, toggle, onHover, onLeave }) {
  const { node, depth, ancestorLines, isLast } = row;
  const color = colorForKind(node.kind);
  const isSelected = selectedId === node.span_id;
  const isError = node.status === 'error';
  const hasChildren = (node.children || []).length > 0;
  const isOpen = !collapsed.has(node.span_id);

  const tokens = tokensOf(node);
  const hasTokens = tokens.total > 0;
  const costStr = money(subtreeCost(node));
  const sep = <span aria-hidden className="text-[color:var(--text-disabled)]">·</span>;
  const track = (e) => onHover(node, e.clientX, e.clientY);
  // A collapsed branch hiding a failure still says so.
  const failureHidden = hasChildren && !isOpen && hasFailedDescendant(node);

  return (
    <div
      onClick={() => onSelect(node)}
      onMouseEnter={track}
      onMouseMove={track}
      onMouseLeave={onLeave}
      className={`group flex cursor-pointer items-center gap-1.5 pr-2 text-[12px] transition-colors ${rowTone(isSelected, isError)}`}
      style={{ minHeight: 34, ...errorEdge(isError) }}
    >
      {/* Tree connector lines */}
      <div className="flex self-stretch items-stretch flex-none">
        <Guides depth={depth} ancestorLines={ancestorLines} isLast={isLast} />
      </div>

      {/* Kind icon: full row height, so the stem can reach the row's bottom */}
      <span className="relative flex w-5 flex-none items-center justify-center self-stretch" title={color.label}>
        <SpanKindIcon kind={node.kind} size={14} />
        {row.stem ? <Stem /> : null}
      </span>

      {/* Name + meta */}
      <div className="min-w-0 flex-1 leading-tight">
        <div className="flex items-center gap-1.5">
          <span className={`truncate text-[12px] text-[color:var(--text-primary)] ${node._wrapper ? 'font-semibold' : ''}`}>{node.name || '(span)'}</span>
          {node.prompt_name ? (
            <span
              className="flex-none rounded bg-surface-hover px-1 text-[10px] font-medium text-[color:var(--text-secondary)]"
              title={`Ran managed prompt ${node.prompt_name}${node.prompt_version_hash ? ` @ ${String(node.prompt_version_hash).slice(0, 8)}` : ''}`}
            >
              {node.prompt_name}
            </span>
          ) : null}
          {isError ? (
            <span
              className="flex-none rounded bg-[color:var(--feedback-error-bg)] px-1 text-[10px] font-medium text-[color:var(--feedback-error)]"
              title={[node.error_type, node.status_code && `status ${node.status_code}`, node.error_message].filter(Boolean).join(' · ')}
            >
              {node.error_type
                ? `${node.error_type}${node.status_code ? ` ${node.status_code}` : ''}`
                : 'error'}
            </span>
          ) : null}
        </div>
        {/* Duration · Σ tokens · cost, rounded; the hover card holds the
            input/output split and the exact figures. */}
        <div className="mt-px flex flex-wrap items-center gap-x-1.5 gap-y-0 text-[11px] tabular-nums">
          <span style={{ color: durationColor(node.duration_ms, maxMs) }}>{ms(node.duration_ms)}</span>
          {hasTokens ? (
            <>
              {sep}
              <TokenSum total={tokens.total} />
            </>
          ) : null}
          {costStr ? (
            <>
              {sep}
              <span className="text-[color:var(--text-muted)]">{costStr}</span>
            </>
          ) : null}
        </div>
      </div>

      {failureHidden ? <FailureDot /> : null}
      {/* Collapse / expand: right edge */}
      {hasChildren ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggle(node.span_id); }}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
          className={`${failureHidden ? '' : 'ml-auto '}flex h-5 w-5 flex-none items-center justify-center rounded text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)]`}
        >
          <ChevronRight size={12} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        </button>
      ) : (
        <span className="ml-auto h-5 w-5 flex-none" aria-hidden />
      )}
    </div>
  );
}

/**
 * What a timeline row holds that its one line doesn't: duration, when it
 * started, the token split and what the cost covers. The row itself shows only
 * total tokens and cost, so a long bar can no longer push them out of view.
 */
function SpanHoverCard({ hover, min, maxMs }) {
  const { node, x, y } = hover;
  const color = colorForKind(node.kind);
  const tokens = tokensOf(node);
  const cost = subtreeCost(node);
  const costStr = cost > 0 ? moneyExact(cost) : null;
  const hasChildren = (node.children || []).length > 0;
  const start = new Date(node.started_at).getTime();
  const offset = Number.isFinite(start) ? start - min : null;
  const isErr = node.status === 'error';

  const W = 248;
  const H = 190;
  const left = x + 14 + W > window.innerWidth ? Math.max(8, x - 14 - W) : x + 14;
  const top = y + 16 + H > window.innerHeight ? Math.max(8, y - 12 - H) : y + 16;

  const line = (label, value, style) => (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[11px] text-[color:var(--text-muted)]">{label}</span>
      <span className="flex-none tabular-nums text-[11px] font-medium text-[color:var(--text-primary)]" style={style}>{value}</span>
    </div>
  );

  return createPortal(
    <div className="pointer-events-none fixed z-[130]" style={{ left, top, width: W }}>
      <div className="overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] shadow-token-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="flex items-center gap-1.5 border-b border-[color:var(--border-subtle)] px-3 py-1.5">
          <SpanKindIcon kind={node.kind} size={13} />
          <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[color:var(--text-primary)]">{node.name || '(span)'}</span>
          <span className="flex-none text-[10px] font-semibold uppercase tracking-wide" style={{ color: color.color }}>{color.label}</span>
        </div>
        <div className="space-y-0.5 px-3 py-2">
          {line('Duration', ms(node.duration_ms), { color: durationColor(node.duration_ms, maxMs) })}
          {offset != null ? line('Starts at', `+${ms(offset)}`) : null}
          {tokens.total > 0 ? (
            <>
              {line('Input tokens', fmtInt(tokens.input))}
              {line('Output tokens', fmtInt(tokens.output))}
              {line('Total tokens', fmtInt(tokens.total))}
            </>
          ) : null}
          {costStr ? line(hasChildren ? 'Cost incl. children' : 'Cost', costStr) : null}
          {isErr ? line('Status', node.error_type || 'error', { color: 'var(--feedback-error)' }) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function TimelineRow({ row, min, pxPerMs, leftWidth, timelineWidth, onSelect, selectedSpanId, collapsed, toggle, onHover, onLeave }) {
  const { node, depth, ancestorLines, isLast } = row;
  const color = colorForKind(node.kind);
  const isSel = selectedSpanId === node.span_id;
  const isErr = node.status === 'error';
  const hasChildren = (node.children || []).length > 0;
  const isOpen = !collapsed.has(node.span_id);
  const start = new Date(node.started_at).getTime();
  const end = node.ended_at ? new Date(node.ended_at).getTime() : start + (node.duration_ms || 0);
  const barLeft = Math.max(0, (start - min) * pxPerMs);
  const barW = Math.max(2, (end - start) * pxPerMs);
  const costStr = money(subtreeCost(node));
  const tokens = tokensOf(node);
  // Sticky left column needs an opaque bg so bars scroll under it.
  // (--feedback-error-bg is an opaque hex, so it works here too.)
  const leftBg = isSel
    ? 'bg-[color:var(--surface-hover)]'
    : isErr ? 'bg-[color:var(--feedback-error-bg)]' : 'bg-surface group-hover:bg-[color:var(--surface-sunken)]';
  const track = (e) => onHover(node, e.clientX, e.clientY);

  return (
    <div
      onClick={() => onSelect(node)}
      onMouseEnter={track}
      onMouseMove={track}
      onMouseLeave={onLeave}
      className={`group flex cursor-pointer ${rowTone(isSel, isErr)}`}
      style={{ height: ROW_HEIGHT }}
    >
      {/* Sticky tree column */}
      <div className={`sticky left-0 z-10 flex items-center gap-1.5 border-r border-[color:var(--border-default)] pl-1 pr-1.5 ${leftBg}`} style={{ width: leftWidth, ...errorEdge(isErr) }}>
        <div className="flex h-full items-stretch"><Guides depth={depth} ancestorLines={ancestorLines} isLast={isLast} /></div>
        <span className="relative flex w-5 flex-none items-center justify-center self-stretch" title={color.label}>
          <SpanKindIcon kind={node.kind} size={14} />
          {row.stem ? <Stem /> : null}
        </span>
        <span className={`min-w-0 truncate text-[12px] text-[color:var(--text-primary)] ${node._wrapper ? 'font-semibold' : ''}`}>{node.name || '(span)'}</span>
        {node.prompt_name ? (
          <span
            className="flex-none rounded bg-surface-hover px-1 text-[10px] font-medium text-[color:var(--text-secondary)]"
            title={`Ran managed prompt ${node.prompt_name}${node.prompt_version_hash ? ` @ ${String(node.prompt_version_hash).slice(0, 8)}` : ''}`}
          >
            {node.prompt_name}
          </span>
        ) : null}
        {isErr ? <span className="flex-none rounded bg-[color:var(--feedback-error-bg)] px-1 text-[9px] font-medium text-[color:var(--feedback-error)]">error</span> : null}
        {/* Total tokens · cost, on the name's line; the split is in the hover card. */}
        <span className="ml-auto flex flex-none items-center gap-1 whitespace-nowrap text-[10.5px] tabular-nums text-[color:var(--text-muted)]">
          {tokens.total > 0 ? <TokenSum total={tokens.total} short /> : null}
          {tokens.total > 0 && costStr ? <span aria-hidden className="text-[color:var(--text-disabled)]">·</span> : null}
          {costStr ? <span>{costStr}</span> : null}
        </span>
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggle(node.span_id); }}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            className="flex h-4 w-4 flex-none items-center justify-center rounded text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
          >
            <ChevronRight size={13} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
          </button>
        ) : <span className="h-4 w-4 flex-none" aria-hidden />}
      </div>

      {/* Bar column */}
      <div className="relative flex-none" style={{ width: timelineWidth }}>
        <div
          className="absolute top-1/2 -translate-y-1/2 rounded"
          style={{ left: barLeft, width: barW, height: 14, backgroundColor: isErr ? 'var(--feedback-error)' : color.color }}
        />
      </div>
    </div>
  );
}

function TimelineContent({ tree, collapsed, toggle, min, windowSpan, onSelect, selectedSpanId, maxMs, leftWidth = 320, minTimelineWidth = 640 }) {
  const rows = flattenVisible(tree, collapsed);
  // One hover card for the whole timeline, following the pointer.
  const [hover, setHover] = useState(null);
  const onHover = (node, x, y) => setHover({ node, x, y });
  const onLeave = () => setHover(null);
  const pxPerMs = Math.max(computePxPerMs(windowSpan), minTimelineWidth / Math.max(windowSpan, 1));
  const timelineWidth = windowSpan * pxPerMs;
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div style={{ width: leftWidth + timelineWidth }}>
        {/* Header: Name + time axis (sticky to the top) */}
        <div className="sticky top-0 z-20 flex">
          <div className="sticky left-0 z-30 flex items-center border-b border-r border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-2 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]" style={{ width: leftWidth }}>
            Name
          </div>
          <div className="flex-none" style={{ width: timelineWidth }}><TimeAxis totalMs={windowSpan} pxPerMs={pxPerMs} /></div>
        </div>
        {/* Rows */}
        {rows.map((row) => (
          <TimelineRow
            key={row.node.span_id}
            row={row}
            min={min}
            pxPerMs={pxPerMs}
            leftWidth={leftWidth}
            timelineWidth={timelineWidth}
            onSelect={onSelect}
            selectedSpanId={selectedSpanId}
            collapsed={collapsed}
            toggle={toggle}
            onHover={onHover}
            onLeave={onLeave}
          />
        ))}
      </div>
      {hover ? <SpanHoverCard hover={hover} min={min} maxMs={maxMs} /> : null}
    </div>
  );
}

export default function TracePanel({
  spans, spansLoading, selectedSpanId, onSelectSpan,
  query = '',
  // Stems the backend matched on, so this tree and the results dropdown above
  // it agree about what "matches". Empty until the debounced search returns.
  terms = [],
  // Spans (and 'run') whose eval verdicts matched the search: kept in the
  // tree though the words are not in their text.
  scoredIds = null,
  run = null, className = '',
}) {
  const [mode, setMode] = useState('tree');
  // The tree's hover card; the timeline keeps its own.
  const [treeHover, setTreeHover] = useState(null);
  const onTreeHover = (node, x, y) => setTreeHover({ node, x, y });
  const onTreeLeave = () => setTreeHover(null);
  const [maximized, setMaximized] = useState(false);
  // Set of collapsed span_ids — empty means everything is expanded by default.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggle = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const fullTree = useMemo(() => buildTree(spans || []), [spans]);
  const q = (query || '').trim().toLowerCase();

  // Synthetic root representing the run itself.
  //
  // Always the top of the tree, not just while searching. A run whose SDK emits
  // several top-level spans had no shared parent, so the tree opened as a set of
  // disconnected stumps with nothing to descend from — and the run's own
  // input/output was reachable only by clicking away from the tree entirely.
  // With the run as the trunk there is exactly one root, every guide line has
  // something to hang off, and selecting it is the run-level view.
  const runNode = useMemo(() => {
    if (!run) return null;
    const attrs = { ...(run.attributes || {}), ...(run.metadata || {}) };
    return {
      span_id: RUN_NODE_ID,
      // Marks this row as structural rather than a step — flattenVisible skips
      // the indent it would otherwise cost when it wraps a single root span.
      _wrapper: true,
      name: run.agent_name || 'Run',
      // Its own kind, so the trunk never looks like one of its agent spans.
      kind: 'run',
      status: run.status,
      input: run.input,
      output: run.output,
      attributes: Object.keys(attrs).length ? attrs : null,
      started_at: run.started_at,
      ended_at: run.ended_at,
      duration_ms: run.duration_ms,
      input_tokens: run.total_tokens_in,
      output_tokens: run.total_tokens_out,
    };
  }, [run]);

  // When searching, prune to matching spans (+ ancestors) and force every node
  // open so matches are never hidden behind a collapsed parent. Matches are
  // nested under the run node so a run-level match surfaces as the root.
  // Stable identity so the filter memo doesn't recompute on every render just
  // because the parent handed us a fresh array. U+0001 separates: a stem is
  // word characters, so it can never contain one and the split round-trips.
  const termKey = (terms || []).join('\u0001');
  const activeTerms = useMemo(() => (termKey ? termKey.split('\u0001') : []), [termKey]);
  const filteredRoots = useMemo(
    () => filterTree(fullTree, q, activeTerms, scoredIds),
    [fullTree, q, activeTerms, scoredIds],
  );
  const runMatches = q && runNode ? (spanMatches(runNode, q, activeTerms) || !!scoredIds?.has('run')) : false;
  const visibleRoots = q ? filteredRoots : fullTree;
  const tree = runNode ? [{ ...runNode, children: visibleRoots }] : visibleRoots;
  const effectiveCollapsed = q ? NO_COLLAPSE : collapsed;
  const { min, max } = useMemo(() => computeWindow(spans || []), [spans]);
  const windowSpan = max - min || 1;
  // Slowest span — drives the latency heat colour in tree + timeline.
  const maxMs = useMemo(() => Math.max(1, ...(spans || []).map((s) => Number(s.duration_ms) || 0)), [spans]);

  const handleSelect = (node) => {
    // The synthetic run node maps to the run-level view (no span selected).
    if (node.span_id === RUN_NODE_ID) { onSelectSpan(null); return; }
    if (selectedSpanId === node.span_id) onSelectSpan(null);
    else onSelectSpan(node.span_id);
  };

  const isEmpty = !spans || spans.length === 0;
  const spanMatchCount = q ? countNodes(filteredRoots) : null;
  const noMatches = q && !runMatches && spanMatchCount === 0;

  const header = (onMaximize, isMaximized) => (
    <header className="flex items-center justify-between gap-2 border-b border-[color:var(--border-default)] bg-surface px-4 py-1.5 flex-shrink-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
        Trace
        {q && spans != null
          ? ` (${spanMatchCount} of ${spans.length} span${spanMatchCount === 1 ? '' : 's'}${runMatches ? ' + run' : ''})`
          : spans != null ? ` (${spans.length} span${spans.length === 1 ? '' : 's'})` : ''}
        {spansLoading ? <span className="ml-2 font-normal normal-case tracking-normal">loading…</span> : null}
      </p>
      <div className="flex items-center gap-2">
        <div className="inline-flex h-7 overflow-hidden rounded-md border border-[color:var(--border-default)] text-[11px]">
          <button type="button" onClick={() => setMode('tree')} className={`px-3 transition-colors ${mode === 'tree' ? 'bg-surface-hover text-[color:var(--text-primary)] font-medium' : 'bg-surface text-[color:var(--text-secondary)] hover:bg-surface-hover'}`}>Tree</button>
          <button type="button" onClick={() => setMode('timeline')} className={`px-3 transition-colors border-l border-[color:var(--border-default)] ${mode === 'timeline' ? 'bg-surface-hover text-[color:var(--text-primary)] font-medium' : 'bg-surface text-[color:var(--text-secondary)] hover:bg-surface-hover'}`}>Timeline</button>
        </div>
        {mode === 'timeline' && !isEmpty && (
          <button
            type="button"
            onClick={onMaximize}
            title={isMaximized ? 'Close' : 'Expand timeline'}
            className="flex items-center justify-center rounded border border-[color:var(--border-default)] bg-surface p-0.5 text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-sunken)] hover:text-[color:var(--text-primary)]"
          >
            {isMaximized ? <X size={13} /> : <Maximize2 size={13} />}
          </button>
        )}
      </div>
    </header>
  );

  return (
    <>
      {/* rounded-md, matching the meta strip above and the tabs panel beside —
          this was the one rounded-lg card in the drawer. */}
      <div className={`flex flex-col overflow-hidden bg-surface ${className}`}>
        {header(() => setMaximized(true), false)}
        {isEmpty ? (
          <div className="flex h-40 items-center justify-center text-sm text-[color:var(--text-muted)]">
            {spansLoading ? 'Fetching trace…' : 'No spans were captured for this run.'}
          </div>
        ) : noMatches ? (
          <div className="flex h-40 items-center justify-center px-4 text-center text-sm text-[color:var(--text-muted)]">
            No spans match “{query.trim()}”.
          </div>
        ) : mode === 'tree' ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {flattenVisible(tree, effectiveCollapsed).map((row) => (
              // No span selected means the drawer is showing the run, so the
              // run node is what's highlighted — otherwise the tree claims
              // nothing is selected while the panel beside it shows the run.
              <TreeRow key={row.node.span_id} row={row} onSelect={handleSelect} selectedId={selectedSpanId ?? RUN_NODE_ID} maxMs={maxMs} collapsed={effectiveCollapsed} toggle={toggle} onHover={onTreeHover} onLeave={onTreeLeave} />
            ))}
            {treeHover ? <SpanHoverCard hover={treeHover} min={min} maxMs={maxMs} /> : null}
          </div>
        ) : (
          <TimelineContent
            tree={tree}
            collapsed={effectiveCollapsed}
            toggle={toggle}
            min={min}
            windowSpan={windowSpan}
            onSelect={handleSelect}
            selectedSpanId={selectedSpanId}
            maxMs={maxMs}
          />
        )}
      </div>

      {/* Maximize modal: portaled to <body> at a high z-index so it overlays
          the side nav (z-50) and the run popup instead of being clipped/stacked
          behind them. */}
      {maximized && createPortal(
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-[color:var(--surface-overlay)]" onClick={() => setMaximized(false)} />
          <div className="relative flex flex-col bg-surface rounded-xl border border-[color:var(--border-default)] shadow-2xl overflow-hidden" style={{ width: '90vw', height: '80vh' }}>
            {header(() => setMaximized(false), true)}
            <TimelineContent
              tree={tree}
              collapsed={effectiveCollapsed}
              toggle={toggle}
              min={min}
              windowSpan={windowSpan}
              onSelect={handleSelect}
              // Same mapping as the tree: run-level view highlights the run row.
              selectedSpanId={selectedSpanId ?? RUN_NODE_ID}
              maxMs={maxMs}
              leftWidth={380}
              minTimelineWidth={1400}
            />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
